<template>
  <div class="ejecucion-root" data-aos="fade-right">
    <CrudToolbar
      title="Mtto. Correctivo"
      v-model="filtroTexto"
      searchPlaceholder="Buscar por falla, máquina o técnico..."
    >
      <template #extra>
        <CrudFechaRango
          v-model:fechaInicio="filtroFechaDesde"
          v-model:fechaFin="filtroFechaHasta"
        />
      </template>
      <template #actions>
        <button class="btn btn-nuevo btn-header" @click="abrirModalNuevo">
          <i class="bi bi-plus-lg"></i>
          <span class="btn-header-texto ms-2">Registrar correctivo</span>
        </button>
      </template>
    </CrudToolbar>

    <!-- ══════════════════════ TABLA DESKTOP ══════════════════════ -->
    <div class="divisan-table-container shadow-lg d-none d-lg-block">
      <div v-if="cargando" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
        <p class="text-cafe mt-2">Cargando registros...</p>
      </div>

      <table v-else class="table table-divisan align-middle mb-0">
        <thead>
          <tr>
            <th class="ps-4">
              <ColumnFilterMenu
                field="fecha"
                label="Fecha"
                type="date"
                :filterable="false"
                :items="correctivosFiltrados"
                :sortField="ordenCampo"
                :sortAsc="ordenAsc"
                @update:sort="onSort"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="maquina_nombre"
                label="Máquina"
                :items="correctivosBuscados"
                :sortField="ordenCampo"
                :sortAsc="ordenAsc"
                :modelValue="filtroMaquinaSet"
                @update:modelValue="filtroMaquinaSet = $event"
                @update:sort="onSort"
              />
            </th>
            <th>Falla</th>
            <th>Técnico</th>
            <th class="text-center">Tiempo</th>
            <th class="text-center">Refacciones</th>
            <th class="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="cor in correctivosPaginados" :key="cor.id">
            <td class="ps-4">
              <span class="fecha-texto">{{ formatFecha(cor.fecha) }}</span>
            </td>
            <td>
              <span class="fw-bold text-cafe">{{ cor.maquina_nombre }}</span>
              <small v-if="cor.maquina_area" class="d-block text-verde opacity-75">
                {{ cor.maquina_area }}
              </small>
            </td>
            <td>
              <span class="falla-texto">{{ cor.falla }}</span>
            </td>
            <td>
              <span class="small text-cafe">{{ cor.tecnico }}</span>
            </td>
            <td class="text-center">
              <span class="tiempo-badge">{{
                formatTiempo(cor.tiempo_valor, cor.tiempo_unidad)
              }}</span>
            </td>
            <td class="text-center">
              <span v-if="cor.refacciones?.length" class="conteo-badge">
                {{ cor.refacciones.length }}
              </span>
              <span v-else class="text-cafe opacity-30 small">—</span>
            </td>
            <td class="text-center">
              <div class="d-flex justify-content-center gap-2">
                <button
                  class="btn-circular"
                  @click="abrirDetalle(cor)"
                  title="Ver detalle"
                >
                  <i class="bi bi-eye text-dark"></i>
                </button>
                <button
                  class="btn-circular"
                  @click="abrirModalEditar(cor)"
                  title="Editar"
                >
                  <i class="bi bi-pencil-square text-verde"></i>
                </button>
                <button
                  class="btn-circular text-danger"
                  @click="confirmarEliminar(cor)"
                  title="Eliminar"
                >
                  <i class="bi bi-trash3-fill"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="correctivosPaginados.length === 0">
            <td colspan="7" class="text-center py-5 text-cafe opacity-40 fw-bold">
              No se encontraron registros
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Paginación -->
      <div
        v-if="totalPaginas > 1"
        class="pagination-wrapper d-flex justify-content-between align-items-center px-4 py-3"
      >
        <small class="text-cafe fw-bold"
          >Página {{ paginaActual }} de {{ totalPaginas }}</small
        >
        <div class="d-flex gap-2">
          <button
            class="btn-pag"
            :disabled="paginaActual === 1"
            @click="cambiarPagina(paginaActual - 1)"
          >
            <i class="bi bi-chevron-left"></i>
          </button>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="cambiarPagina(paginaActual + 1)"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- ══════════════════════ CARDS MÓVIL ══════════════════════ -->
    <div class="d-lg-none">
      <div
        v-for="cor in correctivosFiltrados"
        :key="'mob-' + cor.id"
        class="divisan-mobile-card mb-3 p-3 shadow-sm"
      >
        <div class="d-flex justify-content-between mb-1">
          <span class="fw-bold text-cafe">{{ cor.maquina_nombre }}</span>
          <span class="fecha-texto">{{ formatFecha(cor.fecha) }}</span>
        </div>
        <p class="falla-texto mb-1">{{ cor.falla }}</p>
        <small class="text-cafe opacity-70"
          >{{ cor.tecnico }} ·
          {{ formatTiempo(cor.tiempo_valor, cor.tiempo_unidad) }}</small
        >
        <div class="d-flex justify-content-end gap-3 pt-2 border-top mt-2">
          <i class="bi bi-eye text-dark fs-5" @click="abrirDetalle(cor)"></i>
          <i
            class="bi bi-pencil-square text-verde fs-5"
            @click="abrirModalEditar(cor)"
          ></i>
          <i
            class="bi bi-trash3-fill text-danger fs-5"
            @click="confirmarEliminar(cor)"
          ></i>
        </div>
      </div>
    </div>

    <!-- ══════════════════════ MODAL CREAR / EDITAR ══════════════════════ -->
    <div class="modal fade" id="modalCorrectivo" tabindex="-1" ref="modalFormEl">
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="fw-bold text-cafe">
              {{ editando ? "Editar registro correctivo" : "Nuevo registro correctivo" }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <form @submit.prevent="guardar" class="modal-form-scrollable">
            <div class="modal-body">
              <div class="row g-3">
                <!-- Fecha -->
                <div class="col-12 col-md-6">
                  <label class="form-label label-divisan">Fecha de la falla</label>
                  <input
                    v-model="form.fecha"
                    type="date"
                    :max="hoyStr"
                    class="form-control divisan-input-modal"
                    required
                  />
                </div>

                <!-- Máquina -->
                <div class="col-12 col-md-6" v-click-outside="cerrarModalMaquina">
                  <label class="form-label label-divisan">Máquina</label>
                  <div class="custom-select-wrapper">
                    <div
                      class="form-control divisan-input-modal custom-select-input modal-display"
                      @click="abiertoModalMaquina = !abiertoModalMaquina"
                    >
                      <span v-if="form.maquina" class="text-cafe">
                        {{
                          catMaquinas.find((m) => m.id === form.maquina)?.nombre ||
                          "Selecciona..."
                        }}
                      </span>
                      <span v-else class="text-muted">Selecciona una máquina</span>
                    </div>
                    <i
                      class="bi custom-select-arrow"
                      :class="abiertoModalMaquina ? 'bi-chevron-up' : 'bi-chevron-down'"
                      @mousedown.prevent="abiertoModalMaquina = !abiertoModalMaquina"
                      style="pointer-events: auto; cursor: pointer"
                    ></i>
                    <div v-if="abiertoModalMaquina" class="custom-dropdown">
                      <div
                        v-for="m in catMaquinas"
                        :key="m.id"
                        class="custom-dropdown-item"
                        :class="{ selected: form.maquina === m.id }"
                        @mousedown.prevent="
                          form.maquina = m.id;
                          abiertoModalMaquina = false;
                        "
                      >
                        {{ m.nombre }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Falla -->
                <div class="col-12">
                  <label class="form-label label-divisan">Descripción de la falla</label>
                  <textarea
                    v-model="form.falla"
                    class="form-control divisan-input-modal"
                    rows="3"
                    placeholder="Describe qué falló, síntomas observados, condiciones..."
                    required
                  ></textarea>
                </div>

                <!-- Reparación -->
                <div class="col-12">
                  <label class="form-label label-divisan"
                    >Descripción de la reparación</label
                  >
                  <textarea
                    v-model="form.reparacion"
                    class="form-control divisan-input-modal"
                    rows="3"
                    placeholder="Describe qué se hizo para resolver la falla..."
                    required
                  ></textarea>
                </div>

                <!-- Operador -->
                <div class="col-12 col-md-6">
                  <label class="form-label label-divisan">Operador que reportó</label>
                  <input
                    v-model="form.operador"
                    type="text"
                    class="form-control divisan-input-modal"
                    placeholder="Nombre del operador"
                    required
                  />
                </div>

                <!-- Técnico -->
                <div class="col-12 col-md-6">
                  <label class="form-label label-divisan">Técnico que atendió</label>
                  <input
                    v-model="form.tecnico"
                    type="text"
                    class="form-control divisan-input-modal"
                    placeholder="Nombre del técnico"
                    required
                  />
                </div>

                <!-- Tiempo -->
                <div class="col-12">
                  <label class="form-label label-divisan">Tiempo de intervención</label>
                  <div class="d-flex gap-2">
                    <input
                      v-model.number="form.tiempo_valor"
                      type="number"
                      step="0.5"
                      min="0.5"
                      class="form-control divisan-input-modal"
                      placeholder="Ej. 2.5"
                      required
                    />
                    <div
                      class="custom-select-wrapper"
                      style="max-width: 130px"
                      v-click-outside="cerrarModalTiempo"
                    >
                      <div
                        class="form-control divisan-input-modal custom-select-input modal-display"
                        @click="abiertoModalTiempo = !abiertoModalTiempo"
                      >
                        <span v-if="form.tiempo_unidad === 'MINUTOS'">Minutos</span>
                        <span v-else-if="form.tiempo_unidad === 'HORAS'">Horas</span>
                        <span v-else-if="form.tiempo_unidad === 'DIAS'">Días</span>
                      </div>
                      <i
                        class="bi custom-select-arrow"
                        :class="abiertoModalTiempo ? 'bi-chevron-up' : 'bi-chevron-down'"
                        @mousedown.prevent="abiertoModalTiempo = !abiertoModalTiempo"
                        style="pointer-events: auto; cursor: pointer"
                      ></i>
                      <div v-if="abiertoModalTiempo" class="custom-dropdown">
                        <div
                          v-for="op in opcionesTiempoUnidad"
                          :key="op.value"
                          class="custom-dropdown-item"
                          :class="{ selected: form.tiempo_unidad === op.value }"
                          @mousedown.prevent="
                            form.tiempo_unidad = op.value;
                            abiertoModalTiempo = false;
                          "
                        >
                          {{ op.label }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Refacciones -->
                <div class="col-12">
                  <hr class="border-verde" />
                  <label class="form-label label-divisan">
                    <i class="bi bi-box2 me-1"></i>Refacciones utilizadas
                    <span class="text-cafe opacity-50 small">(opcional)</span>
                  </label>

                  <!-- Selector agregar refacción -->
                  <div class="d-flex gap-2 mb-2">
                    <div
                      class="custom-select-wrapper flex-grow-1"
                      v-click-outside="cerrarDropRefaccion"
                    >
                      <div
                        class="form-control divisan-input-modal custom-select-input modal-display"
                        @click="abiertoDropRefaccion = !abiertoDropRefaccion"
                      >
                        <span v-if="refaccionSeleccionada" class="text-cafe">{{
                          refaccionSeleccionada.nombre_refaccion
                        }}</span>
                        <span v-else class="text-muted">Refacción...</span>
                      </div>
                      <i
                        class="bi custom-select-arrow"
                        :class="
                          abiertoDropRefaccion ? 'bi-chevron-up' : 'bi-chevron-down'
                        "
                        @mousedown.prevent="abiertoDropRefaccion = !abiertoDropRefaccion"
                        style="pointer-events: auto; cursor: pointer"
                      ></i>
                      <div v-if="abiertoDropRefaccion" class="custom-dropdown">
                        <div
                          v-for="r in catRefacciones"
                          :key="r.id"
                          class="custom-dropdown-item"
                          :class="{ selected: refaccionSeleccionada?.id === r.id }"
                          @mousedown.prevent="
                            refaccionSeleccionada = r;
                            abiertoDropRefaccion = false;
                          "
                        >
                          {{ r.nombre_refaccion }}
                        </div>
                      </div>
                    </div>
                    <input
                      v-model.number="cantidadRefaccion"
                      type="number"
                      min="1"
                      class="form-control divisan-input-modal"
                      style="max-width: 80px"
                      placeholder="Cant."
                    />
                    <button
                      type="button"
                      class="btn btn-verde-sm"
                      @click="agregarRefaccionBorrador"
                      :disabled="!refaccionSeleccionada || !cantidadRefaccion"
                    >
                      <i class="bi bi-plus-lg"></i>
                    </button>
                  </div>

                  <!-- Lista de refacciones en borrador -->
                  <div v-if="form.refacciones.length" class="refacciones-borrador">
                    <div
                      v-for="(r, idx) in form.refacciones"
                      :key="idx"
                      class="refaccion-item"
                    >
                      <span class="flex-grow-1 small fw-bold text-cafe">{{
                        r.nombre
                      }}</span>
                      <span class="cantidad-badge">× {{ r.cantidad }}</span>
                      <button
                        type="button"
                        class="btn-quitar"
                        @click="quitarRefaccion(idx)"
                      >
                        <i class="bi bi-x-lg"></i>
                      </button>
                    </div>
                  </div>
                  <p v-else class="small text-cafe opacity-40 mb-0">
                    Sin refacciones agregadas
                  </p>
                </div>

                <!-- Imágenes -->
                <div class="col-12">
                  <label class="form-label label-divisan">
                    <i class="bi bi-images me-1"></i>Fotos de evidencia
                    <span class="text-cafe opacity-50 small">(opcional)</span>
                  </label>
                  <input
                    type="file"
                    @change="handleImagenes"
                    multiple
                    accept="image/*"
                    class="form-control divisan-input-modal"
                  />
                  <small class="text-cafe opacity-60"
                    >Puedes seleccionar varias fotos.</small
                  >
                </div>
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                data-bs-dismiss="modal"
              >
                Cancelar
              </button>
              <button type="submit" class="btn btn-nuevo py-2 px-4" :disabled="guardando">
                <span
                  v-if="guardando"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                {{ editando ? "Actualizar" : "Guardar registro" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- ══════════════════════ MODAL DETALLE ══════════════════════ -->
    <div
      class="modal fade"
      id="modalDetalleCorrectivo"
      tabindex="-1"
      ref="modalDetalleEl"
    >
      <div class="modal-dialog modal-md modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-1">
            <div>
              <h5 class="fw-bold text-cafe mb-0">{{ seleccionado?.maquina_nombre }}</h5>
              <small class="fecha-texto">{{ formatFecha(seleccionado?.fecha) }}</small>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body pt-2">
            <div class="detalle-grid mb-3">
              <div>
                <span class="det-label">Operador</span>
                <div class="det-valor">{{ seleccionado?.operador }}</div>
              </div>
              <div>
                <span class="det-label">Técnico</span>
                <div class="det-valor">{{ seleccionado?.tecnico }}</div>
              </div>
              <div>
                <span class="det-label">Tiempo</span>
                <div class="det-valor">
                  {{
                    formatTiempo(seleccionado?.tiempo_valor, seleccionado?.tiempo_unidad)
                  }}
                </div>
              </div>
              <div>
                <span class="det-label">Área</span>
                <div class="det-valor">{{ seleccionado?.maquina_area || "—" }}</div>
              </div>
            </div>

            <div class="det-seccion mb-3">
              <span class="det-label">Descripción de la falla</span>
              <div class="det-texto">{{ seleccionado?.falla }}</div>
            </div>

            <div class="det-seccion mb-3">
              <span class="det-label">Reparación realizada</span>
              <div class="det-texto">{{ seleccionado?.reparacion }}</div>
            </div>

            <div v-if="seleccionado?.refacciones?.length" class="det-seccion mb-3">
              <span class="det-label">Refacciones utilizadas</span>
              <div class="mt-1">
                <div
                  v-for="r in seleccionado.refacciones"
                  :key="r.id"
                  class="refaccion-item mt-1"
                >
                  <span class="flex-grow-1 small fw-bold text-cafe">{{
                    r.refaccion_nombre
                  }}</span>
                  <span class="cantidad-badge">× {{ r.cantidad }}</span>
                </div>
              </div>
            </div>

            <div v-if="seleccionado?.imagenes?.length" class="det-seccion">
              <span class="det-label">Evidencia fotográfica</span>
              <div
                id="carouselCorrectivo"
                class="carousel slide mt-2"
                :data-bs-ride="seleccionado.imagenes.length > 1 ? 'carousel' : false"
              >
                <div class="carousel-inner rounded-3">
                  <div
                    v-for="(img, idx) in seleccionado.imagenes"
                    :key="img.id"
                    :class="['carousel-item', { active: idx === 0 }]"
                  >
                    <img
                      :src="img.imagen"
                      class="d-block w-100 object-fit-cover"
                      style="height: 220px; cursor: pointer"
                      @click="abrirImagen(img.imagen)"
                    />
                  </div>
                </div>
                <template v-if="seleccionado.imagenes.length > 1">
                  <button
                    class="carousel-control-prev"
                    type="button"
                    data-bs-target="#carouselCorrectivo"
                    data-bs-slide="prev"
                  >
                    <span class="carousel-control-prev-icon"></span>
                  </button>
                  <button
                    class="carousel-control-next"
                    type="button"
                    data-bs-target="#carouselCorrectivo"
                    data-bs-slide="next"
                  >
                    <span class="carousel-control-next-icon"></span>
                  </button>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import {
  getMaquinas,
  getRefacciones,
  getCorrectivos,
  crearCorrectivo,
  actualizarCorrectivo,
  eliminarCorrectivo,
  agregarRefaccionCorrectivo,
  agregarImagenCorrectivo,
} from "@/utils/api_backend";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import CrudToolbar from "@/components/Shared/CrudToolbar.vue";
import CrudFechaRango from "@/components/Shared/CrudFechaRango.vue";
import ColumnFilterMenu from "@/components/Shared/ColumnFilterMenu.vue";

// ── Catálogos ─────────────────────────────────────────────────
const catMaquinas = ref([]);
const catRefacciones = ref([]);

// ── Lista principal ───────────────────────────────────────────
const correctivos = ref([]);
const cargando = ref(false);
const guardando = ref(false);
const editando = ref(false);
const seleccionado = ref(null);

// ── Filtros ───────────────────────────────────────────────────
const filtroTexto = ref("");
const filtroMaquinaSet = ref(new Set());
const filtroFechaDesde = ref("");
const filtroFechaHasta = ref("");
const paginaActual = ref(1);
const porPagina = ref(10);
const ordenCampo = ref("fecha");
const ordenAsc = ref(false);

function onSort({ field, asc }) {
  ordenCampo.value = field;
  ordenAsc.value = asc;
  paginaActual.value = 1;
}

const hoyStr = new Date().toISOString().split("T")[0];

// ── Dropdowns modal ───────────────────────────────────────────
const abiertoModalMaquina = ref(false);
function cerrarModalMaquina() {
  abiertoModalMaquina.value = false;
}

const abiertoModalTiempo = ref(false);
const opcionesTiempoUnidad = [
  { value: "MINUTOS", label: "Minutos" },
  { value: "HORAS", label: "Horas" },
  { value: "DIAS", label: "Días" },
];
function cerrarModalTiempo() {
  abiertoModalTiempo.value = false;
}

const abiertoDropRefaccion = ref(false);
function cerrarDropRefaccion() {
  abiertoDropRefaccion.value = false;
}

// ── Directiva click-outside ───────────────────────────────────
const vClickOutside = {
  mounted(el, b) {
    el._co = (e) => {
      if (!el.contains(e.target)) b.value(e);
    };
    document.addEventListener("click", el._co);
  },
  unmounted(el) {
    document.removeEventListener("click", el._co);
  },
};

// ── Modales ───────────────────────────────────────────────────
const modalFormEl = ref(null);
const modalDetalleEl = ref(null);
let modalFormInst = null;
let modalDetalleInst = null;

// ── Formulario ────────────────────────────────────────────────
const formVacio = () => ({
  id: null,
  maquina: "",
  fecha: hoyStr,
  falla: "",
  reparacion: "",
  operador: "",
  tecnico: "",
  tiempo_valor: "",
  tiempo_unidad: "HORAS",
  refacciones: [], // [{ id, refaccionId, nombre, cantidad }]
});
const form = ref(formVacio());

// Selección de refacción para agregar
const refaccionSeleccionada = ref("");
const cantidadRefaccion = ref(1);
const selectedImagenes = ref([]);

// ── Watchers ──────────────────────────────────────────────────
watch([filtroTexto, filtroMaquinaSet, filtroFechaDesde, filtroFechaHasta], () => {
  paginaActual.value = 1;
});

// ── Computed ──────────────────────────────────────────────────
const correctivosBuscados = computed(() => {
  const q = filtroTexto.value.toLowerCase();
  return correctivos.value.filter((c) => {
    const matchTexto =
      !q ||
      (c.falla || "").toLowerCase().includes(q) ||
      (c.maquina_nombre || "").toLowerCase().includes(q) ||
      (c.tecnico || "").toLowerCase().includes(q);
    const matchDesde = !filtroFechaDesde.value || c.fecha >= filtroFechaDesde.value;
    const matchHasta = !filtroFechaHasta.value || c.fecha <= filtroFechaHasta.value;
    return matchTexto && matchDesde && matchHasta;
  });
});

const correctivosFiltrados = computed(() =>
  correctivosBuscados.value.filter(
    (c) => !filtroMaquinaSet.value.size || filtroMaquinaSet.value.has(c.maquina_nombre)
  )
);

const correctivosOrdenados = computed(() =>
  [...correctivosFiltrados.value].sort((a, b) => {
    const va = (a[ordenCampo.value] || "").toString();
    const vb = (b[ordenCampo.value] || "").toString();
    if (va < vb) return ordenAsc.value ? -1 : 1;
    if (va > vb) return ordenAsc.value ? 1 : -1;
    return 0;
  })
);

const totalPaginas = computed(() =>
  Math.ceil(correctivosOrdenados.value.length / porPagina.value)
);

const correctivosPaginados = computed(() => {
  const ini = (paginaActual.value - 1) * porPagina.value;
  return correctivosOrdenados.value.slice(ini, ini + porPagina.value);
});

function cambiarPagina(p) {
  if (p >= 1 && p <= totalPaginas.value) paginaActual.value = p;
}

// ── Helpers ───────────────────────────────────────────────────
function abrirImagen(url) {
  window.open(url, "_blank");
}

function formatFecha(f) {
  if (!f) return "—";
  return new Date(f + "T12:00:00").toLocaleDateString("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatTiempo(valor, unidad) {
  if (!valor) return "—";
  const n = parseFloat(valor);
  const labels = { MINUTOS: "min", HORAS: "hr", DIAS: "día" };
  const sufijo = labels[unidad] || unidad;
  // Pluralizar "día" → "días"
  const plural = unidad === "DIAS" && n !== 1 ? "s" : "";
  return `${n % 1 === 0 ? n : n}${sufijo}${plural}`;
}

// ── Carga ─────────────────────────────────────────────────────
async function cargar() {
  cargando.value = true;
  try {
    correctivos.value = await getCorrectivos();
  } catch {
    Swal.fire("Error", "No se pudieron cargar los registros.", "error");
  } finally {
    cargando.value = false;
  }
}

// ── Modales ───────────────────────────────────────────────────
function abrirModalNuevo() {
  editando.value = false;
  form.value = formVacio();
  refaccionSeleccionada.value = "";
  cantidadRefaccion.value = 1;
  selectedImagenes.value = [];
  modalFormInst?.show();
}

function abrirModalEditar(cor) {
  editando.value = true;
  form.value = {
    id: cor.id,
    maquina: cor.maquina,
    fecha: cor.fecha,
    falla: cor.falla,
    reparacion: cor.reparacion,
    operador: cor.operador,
    tecnico: cor.tecnico,
    tiempo_valor: cor.tiempo_valor,
    tiempo_unidad: cor.tiempo_unidad,
    refacciones: (cor.refacciones || []).map((r) => ({
      id: r.id,
      refaccionId: r.refaccion,
      nombre: r.refaccion_nombre,
      cantidad: r.cantidad,
    })),
  };
  refaccionSeleccionada.value = "";
  cantidadRefaccion.value = 1;
  selectedImagenes.value = [];
  modalFormInst?.show();
}

function abrirDetalle(cor) {
  seleccionado.value = cor;
  modalDetalleInst?.show();
}

// ── Refacciones en borrador ───────────────────────────────────
function agregarRefaccionBorrador() {
  if (!refaccionSeleccionada.value || !cantidadRefaccion.value) return;
  const yaExiste = form.value.refacciones.some(
    (r) => r.refaccionId === refaccionSeleccionada.value.id
  );
  if (yaExiste) {
    Swal.fire({
      icon: "warning",
      title: "Atención",
      text: "Esta refacción ya fue agregada.",
      confirmButtonColor: "var(--d-brown)",
    });
    return;
  }
  form.value.refacciones.push({
    id: null,
    refaccionId: refaccionSeleccionada.value.id,
    nombre: refaccionSeleccionada.value.nombre_refaccion,
    cantidad: cantidadRefaccion.value,
  });
  refaccionSeleccionada.value = "";
  cantidadRefaccion.value = 1;
}

function quitarRefaccion(idx) {
  form.value.refacciones.splice(idx, 1);
}

function handleImagenes(e) {
  selectedImagenes.value = Array.from(e.target.files);
}

// ── Guardar ───────────────────────────────────────────────────
async function guardar() {
  guardando.value = true;
  try {
    const payload = {
      maquina: form.value.maquina,
      fecha: form.value.fecha,
      falla: form.value.falla,
      reparacion: form.value.reparacion,
      operador: form.value.operador,
      tecnico: form.value.tecnico,
      tiempo_valor: form.value.tiempo_valor,
      tiempo_unidad: form.value.tiempo_unidad,
    };

    let savedId;
    if (editando.value) {
      await actualizarCorrectivo(form.value.id, payload);
      savedId = form.value.id;
    } else {
      const created = await crearCorrectivo(payload);
      savedId = created.id;
    }

    // Agregar refacciones nuevas (sin id de BD)
    for (const r of form.value.refacciones.filter((r) => !r.id)) {
      await agregarRefaccionCorrectivo(savedId, {
        refaccion: r.refaccionId,
        cantidad: r.cantidad,
      });
    }

    // Subir imágenes
    for (const img of selectedImagenes.value) {
      await agregarImagenCorrectivo(savedId, img);
    }

    await cargar();
    modalFormInst.hide();
    Swal.fire({
      icon: "success",
      title: "Registro guardado",
      text: `Correctivo ${editando.value ? "actualizado" : "creado"} correctamente.`,
      timer: 2000,
      showConfirmButton: false,
    });
  } catch (err) {
    const msg = err?.response?.data
      ? Object.values(err.response.data).flat().join(" ")
      : "Ocurrió un problema al guardar.";
    Swal.fire("Error", msg, "error");
  } finally {
    guardando.value = false;
  }
}

// ── Eliminar ──────────────────────────────────────────────────
async function confirmarEliminar(cor) {
  const res = await Swal.fire({
    title: `¿Eliminar este registro?`,
    text: `${cor.maquina_nombre} — ${formatFecha(cor.fecha)}`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "var(--d-brown)",
    cancelButtonColor: "#6c757d",
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
  });
  if (res.isConfirmed) {
    try {
      await eliminarCorrectivo(cor.id);
      await cargar();
      Swal.fire({
        icon: "success",
        title: "Eliminado",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire("Error", "No se pudo eliminar el registro.", "error");
    }
  }
}

// ── Init ──────────────────────────────────────────────────────
onMounted(async () => {
  modalFormInst = new Modal(modalFormEl.value);
  modalDetalleInst = new Modal(modalDetalleEl.value);
  try {
    const [maq, ref] = await Promise.all([getMaquinas(), getRefacciones()]);
    catMaquinas.value = maq;
    catRefacciones.value = ref;
    await cargar();
  } catch {
    Swal.fire("Error", "Error al inicializar el módulo.", "error");
  }
});
</script>

<style scoped>
.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-nuevo:hover {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.divisan-table-container {
  background-color: var(--d-cream);
  border-radius: 15px;
  overflow: hidden;
}
.table-divisan thead th {
  background: var(--d-cream);
  color: var(--d-brown);
  border-bottom: 3px solid var(--d-brown);
  padding: 12px 15px;
  font-weight: 800;
  text-transform: capitalize;
  border-top: none;
}
.table-divisan td {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  padding: 10px 12px;
}

.fecha-texto {
  color: var(--d-green);
  font-weight: 700;
  font-size: 0.85rem;
}
.falla-texto {
  font-size: 0.85rem;
  color: var(--d-brown);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 280px;
}
.tiempo-badge {
  background: rgba(96, 108, 56, 0.12);
  color: var(--d-green-dark);
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 700;
}
.conteo-badge {
  background: rgba(204, 54, 54, 0.1);
  color: #842029;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 800;
}

.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}
.btn-circular:hover {
  background-color: rgba(96, 108, 56, 0.1);
}

.pagination-wrapper {
  border-top: 1px solid rgba(64, 34, 8, 0.1);
  background-color: rgba(96, 108, 56, 0.05);
}
.btn-pag {
  background: white;
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-pag:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag:disabled {
  opacity: 0.35;
  cursor: default;
}

.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}
.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}
.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
}
.border-verde {
  border-color: var(--d-green) !important;
}

.divisan-mobile-card {
  background-color: var(--d-cream);
  border-radius: 15px;
  border-left: 6px solid #cc3636;
}

/* ── Refacciones en formulario ── */
.refacciones-borrador {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.refaccion-item {
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border: 1px solid rgba(96, 108, 56, 0.15);
  border-radius: 7px;
  padding: 5px 10px;
}
.cantidad-badge {
  font-size: 0.78rem;
  font-weight: 800;
  color: var(--d-green);
}
.btn-quitar {
  background: none;
  border: none;
  color: #a32d2d;
  cursor: pointer;
  font-size: 0.78rem;
  padding: 2px 4px;
  border-radius: 4px;
}
.btn-quitar:hover {
  background: rgba(163, 45, 45, 0.1);
}
.btn-verde-sm {
  background-color: var(--d-green);
  color: var(--d-cream);
  border: none;
  border-radius: 8px;
  padding: 0.4rem 0.7rem;
  cursor: pointer;
}
.btn-verde-sm:hover:not(:disabled) {
  background-color: var(--d-green-dark);
}
.btn-verde-sm:disabled {
  opacity: 0.4;
}

/* ── Detalle ── */
.detalle-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.det-label {
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  color: var(--d-green);
  display: block;
  margin-bottom: 2px;
}
.det-valor {
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--d-brown);
}
.det-seccion {
  border-top: 1px solid rgba(96, 108, 56, 0.15);
  padding-top: 10px;
}
.det-texto {
  font-size: 0.88rem;
  color: var(--d-brown);
  line-height: 1.5;
  margin-top: 4px;
}

/* .imagenes-grid y .img-evidencia reemplazados por carrusel Bootstrap */

/* ── Custom select (modal) ── */
.custom-select-wrapper {
  position: relative;
}
.custom-select-input {
  padding-right: 2.2rem !important;
}
.modal-display {
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  min-height: 38px;
  background-color: white;
}
.custom-select-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
  font-size: 0.8rem;
  cursor: pointer;
}
.custom-dropdown {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  z-index: 1080;
  background: white;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(40, 54, 24, 0.15);
  max-height: 200px;
  overflow-y: auto;
}
.custom-dropdown-item {
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 0.88rem;
  color: var(--d-brown);
  border-bottom: 1px solid rgba(96, 108, 56, 0.08);
  transition: background 0.12s;
}
.custom-dropdown-item:last-child {
  border-bottom: none;
}
.custom-dropdown-item:hover,
.custom-dropdown-item.selected {
  background: rgba(96, 108, 56, 0.12);
}

/* ── Form scrollable ── */
.modal-form-scrollable {
  display: contents;
}

@media (max-width: 768px) {
  .modal-dialog {
    margin: 85px auto 0.5rem !important;
    max-width: calc(100vw - 1rem) !important;
    height: calc(100dvh - 85px - 0.5rem);
    max-height: calc(100dvh - 85px - 0.5rem);
  }
  .modal-dialog-scrollable .modal-content {
    height: 100%;
    max-height: 100%;
    border-radius: 16px !important;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .modal-dialog-scrollable .modal-header,
  .modal-dialog-scrollable .modal-footer {
    flex-shrink: 0;
  }
  .modal-dialog-scrollable .modal-body {
    flex: 1 1 auto;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
}

@media (max-width: 991.98px) {
  .btn-header-texto {
    display: none;
  }
  .btn-header {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}
</style>
